import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Edit2, Trash2, Save, X, Check, Square, CheckSquare, ArrowLeft } from 'lucide-react';
import { api } from './api';

const CATEGORIES = [
  { key: 'trays', label: 'Trays/Instruments', color: 'bg-blue-500' },
  { key: 'drapes', label: 'Drapes/Gowns/Pack', color: 'bg-orange-500' },
  { key: 'gloves', label: 'Gloves', color: 'bg-green-500' },
  { key: 'supplies', label: 'Supplies', color: 'bg-teal-500' },
  { key: 'dressings', label: 'Dressings', color: 'bg-pink-500' },
  { key: 'sutures', label: 'Sutures', color: 'bg-purple-500' },
];

export default function PickingCards({ onBack = () => {} }) {
  const [cards, setCards] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentCard, setCurrentCard] = useState(null);
  const [showSavedToast, setShowSavedToast] = useState(false);

  useEffect(() => {
    loadCards();
  }, []);

  // Push a history entry when opening a card so browser back returns to the list
  useEffect(() => {
    if (currentCard) {
      window.history.pushState({ pickingCard: true }, '');
    }
  }, [currentCard?.id]);

  const handlePopState = useCallback(() => {
    if (currentCard) {
      setCurrentCard(null);
    }
  }, [currentCard]);

  useEffect(() => {
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [handlePopState]);

  const loadCards = async () => {
    try {
      const loadedCards = await api.getPickingCards();
      setCards(loadedCards);
    } catch (error) {
      console.error('Error loading picking cards:', error);
    }
  };

  const saveCard = async (cardData) => {
    try {
      await api.savePickingCard(cardData);
      await loadCards();
    } catch (error) {
      console.error('Error saving picking card:', error);
    }
  };

  const deleteCard = async (cardId) => {
    if (currentCard?.id === cardId) {
      setCurrentCard(null);
    }
    try {
      await api.deletePickingCard(cardId);
      await loadCards();
    } catch (error) {
      console.error('Error deleting picking card:', error);
    }
  };

  const handleCreateCard = () => {
    const newCard = {
      id: Date.now().toString(),
      title: 'New Picking Card',
      date: new Date().toISOString().split('T')[0],
      notes: '',
      sutures: [],
      trays: [],
      gloves: [],
      supplies: [],
      drapes: [],
      dressings: []
    };
    setCurrentCard(newCard);
  };

  const handleSaveCard = async () => {
    if (currentCard) {
      await saveCard(currentCard);
      setShowSavedToast(true);
      setTimeout(() => setShowSavedToast(false), 2000);
    }
  };

  const handleAddItem = (category) => {
    setCurrentCard(prev => ({
      ...prev,
      [category]: [...(prev[category] || []), {
        id: Date.now().toString(),
        description: '',
        quantity: '',
        notes: '',
        picked: false
      }]
    }));
  };

  const handleUpdateItem = (category, itemId, field, value) => {
    setCurrentCard(prev => ({
      ...prev,
      [category]: (prev[category] || []).map(item =>
        item.id === itemId ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleTogglePicked = (category, itemId) => {
    setCurrentCard(prev => {
      const updated = {
        ...prev,
        [category]: (prev[category] || []).map(item =>
          item.id === itemId ? { ...item, picked: !item.picked } : item
        )
      };
      // Auto-save when toggling picked status
      saveCard(updated);
      return updated;
    });
  };

  const handleDeleteItem = (category, itemId) => {
    setCurrentCard(prev => ({
      ...prev,
      [category]: (prev[category] || []).filter(item => item.id !== itemId)
    }));
  };

  const getFilteredCards = () => {
    if (!searchTerm) return cards;
    return cards.filter(c =>
      c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.notes.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const getProgress = (card) => {
    let total = 0;
    let picked = 0;
    CATEGORIES.forEach(cat => {
      const items = card[cat.key] || [];
      total += items.length;
      picked += items.filter(i => i.picked).length;
    });
    return { total, picked, percent: total > 0 ? Math.round((picked / total) * 100) : 0 };
  };

  // CARD LIST VIEW
  if (!currentCard) {
    const filteredCards = getFilteredCards();

    return (
      <div className="p-6 bg-slate-900 min-h-screen">
        <div className="max-w-6xl mx-auto">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <h1 className="text-3xl font-bold text-slate-100">Picking Cards</h1>
              </div>
              <button
                onClick={handleCreateCard}
                className="flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700"
              >
                <Plus size={20} /> New Picking Card
              </button>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-3 text-slate-500" size={20} />
              <input
                type="text"
                placeholder="Search picking cards..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-slate-600 rounded-lg focus:ring-2 focus:ring-teal-500 bg-slate-900 text-slate-100"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCards.map((card) => {
              const progress = getProgress(card);
              return (
                <div key={card.id} className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden hover:border-slate-500 transition-colors">
                  <div className="p-4">
                    <div className="cursor-pointer" onClick={() => setCurrentCard(card)}>
                      <h3 className="text-xl font-bold text-slate-100 mb-1">{card.title}</h3>
                      <p className="text-sm text-slate-500 mb-3">{card.date}</p>

                      {/* Progress bar */}
                      <div className="mb-3">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-slate-400">Progress</span>
                          <span className="text-slate-100 font-medium">{progress.picked}/{progress.total}</span>
                        </div>
                        <div className="w-full bg-slate-700 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${progress.percent === 100 ? 'bg-green-500' : 'bg-teal-500'}`}
                            style={{ width: `${progress.percent}%` }}
                          />
                        </div>
                      </div>

                      {card.notes && (
                        <p className="text-sm text-gray-700 mb-3 line-clamp-2">{card.notes}</p>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => setCurrentCard(card)}
                        className="flex-1 flex items-center justify-center gap-1 bg-teal-600 text-white px-3 py-2 rounded hover:bg-teal-700 text-sm"
                      >
                        <Edit2 size={14} /> Edit
                      </button>
                      <button
                        onClick={() => deleteCard(card.id)}
                        className="bg-red-600 text-white px-3 py-2 rounded hover:bg-red-700"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {filteredCards.length === 0 && (
            <div className="bg-white rounded-lg shadow-lg p-12 text-center">
              <p className="text-slate-500 text-lg">No picking cards found. Create your first one!</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // CARD EDITOR VIEW
  const progress = getProgress(currentCard);

  return (
    <div className="bg-slate-900 min-h-screen">
      {/* Header */}
      <div className="bg-slate-900 border-b border-slate-800 p-4 flex justify-between items-center">
        <button
          onClick={() => setCurrentCard(null)}
          className="flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium"
        >
          <X size={20} /> Back to Picking Cards
        </button>
        <div className="flex items-center gap-4">
          <div className="text-sm text-slate-400">
            {progress.picked}/{progress.total} picked ({progress.percent}%)
          </div>
          <div className="relative flex items-center">
            {showSavedToast && (
              <span className="absolute -top-9 left-1/2 -translate-x-1/2 bg-green-600 text-white text-sm px-3 py-1 rounded-lg shadow whitespace-nowrap">
                Card saved!
              </span>
            )}
            <button
              onClick={handleSaveCard}
              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
            >
              <Save size={18} /> Save Card
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-4xl mx-auto bg-slate-900 min-h-screen">
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 mb-6">
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Card Title</label>
              <input
                type="text"
                value={currentCard.title}
                onChange={(e) => setCurrentCard({ ...currentCard, title: e.target.value })}
                className="w-full px-4 py-2 border border-slate-600 rounded-lg focus:ring-2 focus:ring-teal-500 bg-slate-900 text-slate-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
              <input
                type="date"
                value={currentCard.date}
                onChange={(e) => setCurrentCard({ ...currentCard, date: e.target.value })}
                className="w-full px-4 py-2 border border-slate-600 rounded-lg focus:ring-2 focus:ring-teal-500 bg-slate-900 text-slate-100"
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea
              value={currentCard.notes}
              onChange={(e) => setCurrentCard({ ...currentCard, notes: e.target.value })}
              className="w-full px-4 py-2 border border-slate-600 rounded-lg focus:ring-2 focus:ring-teal-500 bg-slate-900 text-slate-100"
              rows="3"
              placeholder="Add any notes..."
            />
          </div>
        </div>

        {/* Categories */}
        {CATEGORIES.map((category) => (
          <div key={category.key} className="bg-slate-800 border border-slate-700 rounded-lg p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-4 h-4 rounded ${category.color}`} />
                <label className="text-lg font-bold text-slate-100">{category.label}</label>
              </div>
              <button
                onClick={() => handleAddItem(category.key)}
                className="flex items-center gap-1 bg-teal-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-teal-700"
              >
                <Plus size={16} /> Add
              </button>
            </div>

            <div className="space-y-3">
              {(currentCard[category.key] || []).map((item) => (
                <div
                  key={item.id}
                  className={`p-4 rounded-lg border-2 transition-colors ${
                    item.picked
                      ? 'bg-slate-900 border-green-700'
                      : 'bg-slate-900 border-slate-700'
                  }`}
                >
                  <div className="grid grid-cols-12 gap-3 items-center">
                    <button
                      onClick={() => handleTogglePicked(category.key, item.id)}
                      className={`col-span-1 p-2 rounded ${
                        item.picked
                          ? 'text-green-600 hover:text-green-700'
                          : 'text-slate-500 hover:text-slate-400'
                      }`}
                    >
                      {item.picked ? <CheckSquare size={24} /> : <Square size={24} />}
                    </button>
                    <input
                      type="text"
                      value={item.description}
                      onChange={(e) => handleUpdateItem(category.key, item.id, 'description', e.target.value)}
                      placeholder="Description"
                      className={`col-span-4 px-3 py-2 border border-slate-600 rounded focus:ring-2 focus:ring-teal-500 bg-slate-900 text-slate-100 ${
                        item.picked ? 'line-through text-slate-500' : ''
                      }`}
                    />
                    <input
                      type="text"
                      value={item.quantity}
                      onChange={(e) => handleUpdateItem(category.key, item.id, 'quantity', e.target.value)}
                      placeholder="Qty"
                      className={`col-span-2 px-3 py-2 border border-slate-600 rounded focus:ring-2 focus:ring-teal-500 bg-slate-900 text-slate-100 ${
                        item.picked ? 'line-through text-slate-500' : ''
                      }`}
                    />
                    <textarea
                      value={item.notes}
                      onChange={(e) => {
                        handleUpdateItem(category.key, item.id, 'notes', e.target.value);
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                      }}
                      ref={(el) => {
                        if (el) {
                          el.style.height = 'auto';
                          el.style.height = el.scrollHeight + 'px';
                        }
                      }}
                      placeholder="Notes"
                      rows={1}
                      className={`col-span-4 px-3 py-2 border border-slate-600 rounded focus:ring-2 focus:ring-teal-500 bg-slate-900 text-slate-100 resize-none overflow-hidden ${
                        item.picked ? 'line-through text-slate-500' : ''
                      }`}
                      style={{ minHeight: '42px' }}
                    />
                    <button
                      onClick={() => handleDeleteItem(category.key, item.id)}
                      className="col-span-1 bg-red-500 text-white p-2 rounded hover:bg-red-600 flex items-center justify-center"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}

              {(currentCard[category.key] || []).length === 0 && (
                <p className="text-slate-500 text-center py-4">No items yet. Click "Add" to add items.</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
